// meter-rules-loader.js — loader for the meter rule data layer (Brief 054).
//
// content/meter-rules.json is an itemId → rule map extracted from the iOS
// plist corpus by tools/extract-meter-rules.mjs. Brief 055's meter engine +
// UI consult it to adjust an affected item's required playcount after the
// learner answers a Dialogue Comprehension / Listening Stage / BrainSoaking
// meter. Meters are NOT exercises — see docs/CMS-CONTRACT-FOR-BRYAN.md §4.
//
// Loaded once at boot via loadMeterRules(); getMeterRule() then queries it
// synchronously. Mirrors the loader pattern in content-items.js and
// item-titles.js.
//
// Null-rule handling: un-configured CMS templates are emitted with null
// thresholds (and often a null affectedItemId). They are represented here
// faithfully — the engine (Brief 055) skips null rules at evaluation time.

const CONTENT_BASE = "/content";

/**
 * @typedef {object} MeterThreshold
 * @property {number|null} score             pass-score threshold (percentage).
 * @property {number|null} playcountIncrease required-play bump when score ≤ threshold.
 *
 * @typedef {object} MeterStrings
 * @property {{ title: string }} en
 * @property {{ title: string }} zh
 *
 * @typedef {object} MeterRule
 * @property {number}      itemId                the meter's own item id (the map key; stamped on at load).
 * @property {"dialog-comp"|"listening"|"brainsoaking"} meterType
 * @property {number|null} affectedItemId       item whose playcount the meter adjusts.
 * @property {0|1|2|3}     affectedItemDirection 0=preceding,1=following,2=before,3=after.
 * @property {MeterThreshold[]} thresholds       non-empty; entries may be all-null (template).
 * @property {MeterStrings} strings
 */

/** @type {Record<string, MeterRule> | null} */
let cache = null;
/** @type {Promise<void> | null} */
let promise = null;

/**
 * Stamp each rule with its own `itemId` (the map key) so a rule passed around
 * on its own — to the engine, the UI, or telemetry — still knows which meter it
 * is. The meter engine's telemetry path (Function B) reads `meterRule.itemId`.
 * @param {Record<string, MeterRule>} m
 * @returns {Record<string, MeterRule>}
 */
function stampIds(m) {
  for (const key of Object.keys(m)) {
    const rule = m[key];
    if (rule && typeof rule === "object") rule.itemId = Number(key);
  }
  return m;
}

/**
 * Synchronously inject the meter-rules map (test seam / non-fetch boot).
 * @param {Record<string, MeterRule> | null} m
 */
export function setMeterRules(m) {
  cache = m ? stampIds(m) : m;
  promise = Promise.resolve();
}

/**
 * Fetch content/meter-rules.json once and cache it. Safe to call repeatedly —
 * subsequent calls return the same promise. If the file is missing or fails to
 * parse, the cache is set to {} and getMeterRule() returns null for every item.
 * @returns {Promise<void>}
 */
export function loadMeterRules() {
  if (promise) return promise;
  promise = fetch(`${CONTENT_BASE}/meter-rules.json`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((j) => {
      cache = (j && typeof j === "object")
        ? stampIds(/** @type {Record<string, MeterRule>} */ (j))
        : {};
    });
  return promise;
}

/**
 * Synchronous lookup of a single meter rule. Returns the record or null if the
 * item is not a meter (or has no rule). Accepts numeric or string ids.
 * @param {number|string} itemId
 * @returns {MeterRule | null}
 */
export function getMeterRule(itemId) {
  if (!cache) throw new Error("[meter-rules] not loaded — call loadMeterRules() first");
  return cache[String(itemId)] ?? null;
}

/**
 * Reverse lookup by playcount target. Given an item, return every meter whose
 * `affectedItemId` is that item — i.e. meters that adjust THAT item's required
 * playcount. This answers "which meters grade item X", which is NOT the same as
 * "which meter fires when X completes" — see `meterRuleAfterItem` for the
 * trigger model and Brief 107 for why the two must not be conflated.
 *
 * @param {number|string} itemId
 * @returns {MeterRule[]}
 */
export function meterRulesForItem(itemId) {
  if (!cache) throw new Error("[meter-rules] not loaded — call loadMeterRules() first");
  const target = Number(itemId);
  const out = [];
  for (const key of Object.keys(cache)) {
    const rule = cache[key];
    if (rule && rule.affectedItemId === target) out.push(rule);
  }
  return out;
}

/**
 * Parse the numeric id from a lesson-item id, which the pathway JSON encodes as
 * `"item_103520"` (string) while routes / the engine use the bare number. Plain
 * numbers and numeric strings pass through. Returns NaN when there is no number.
 * @param {string|number|null|undefined} id
 * @returns {number}
 */
function numericItemId(id) {
  if (typeof id === "number") return id;
  if (typeof id === "string") {
    const m = /(\d+)/.exec(id);
    if (m) return Number(m[1]);
  }
  return NaN;
}

/**
 * Positional meter trigger (Brief 107). A meter fires when the learner completes
 * the item that DIRECTLY PRECEDES it in the lesson sequence — on iOS meters are
 * inline lesson items that present themselves the moment the learner reaches
 * them, so the trigger is purely positional. Given the ordered (unfiltered)
 * lesson items and the just-completed item, this returns the meter rule for the
 * immediately-following item, or null when the next item is not a meter.
 *
 * Why NOT reverse-lookup by `affectedItemId` (the pre-107 trigger): a meter's
 * `affectedItemId` + direction name the item whose PLAYCOUNT it adjusts, which
 * need not be the item that precedes it. Several meters can share one
 * `affectedItemId` (e.g. lesson 103517 has three meters all bumping the song
 * 103520 from different positions), so keying the trigger off `affectedItemId`
 * collides and fires whichever rule sorts first — the Brief 107 bug, where the
 * song's completion fired a Dialog Comp meter instead of the Listening Stage
 * meter that actually sits after it.
 *
 * @param {Array<{ id?: string|number }>|null|undefined} lessonItems ordered lesson items (unfiltered).
 * @param {number|string} completedItemId
 * @returns {MeterRule | null}
 */
export function meterRuleAfterItem(lessonItems, completedItemId) {
  if (!cache) throw new Error("[meter-rules] not loaded — call loadMeterRules() first");
  if (!Array.isArray(lessonItems)) return null;
  const target = numericItemId(completedItemId);
  if (!Number.isFinite(target)) return null;
  const i = lessonItems.findIndex((it) => numericItemId(it?.id) === target);
  if (i < 0 || i + 1 >= lessonItems.length) return null;
  return getMeterRule(numericItemId(lessonItems[i + 1]?.id));
}
